import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { Building2, Plus, Edit, Trash2, Search, Star, FileText, Link2, MapPin, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const WORK_CATEGORIES = [
  "Earthwork",
  "Concrete Work",
  "Steel/Reinforcement",
  "Road Work",
  "Drainage",
  "Electrical",
  "Plumbing",
  "Painting",
  "Carpentry",
  "Masonry",
  "Landscaping",
  "Equipment Rental",
  "Labour Supply",
  "Transportation",
  "Other"
];

const ContractorManagement = () => {
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rateContracts, setRateContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("contractors");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  
  // Contractor Dialog
  const [contractorDialogOpen, setContractorDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [contractorForm, setContractorForm] = useState({
    contractor_code: "",
    name: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gst_number: "",
    pan_number: "",
    bank_name: "",
    bank_account: "",
    ifsc_code: "",
    work_categories: [],
    registration_number: "",
    license_number: "",
    license_expiry: ""
  });

  // Rate Contract Dialog
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateForm, setRateForm] = useState({
    contractor_id: "",
    project_id: "",
    work_category: "",
    description: "",
    unit: "Cum",
    rate: "",
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: "",
    terms_conditions: ""
  });

  const currentUser = getUser() || {};
  // Allow Master Admin, Management, and users with DPR module access to add/edit contractors
  const hasDPRAccess = currentUser.permissions?.modules?.includes("dpr") || false;
  const canEdit = currentUser.role === "Master Admin" || currentUser.role === "Management" || hasDPRAccess;
  const canDelete = currentUser.role === "Master Admin" || currentUser.role === "Management"; // Only Management can delete

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contRes, projRes, rateRes, statsRes] = await Promise.all([
        apiClient.get('/contractors'),
        apiClient.get('/projects'),
        apiClient.get('/rate-contracts'),
        apiClient.get('/contractors/stats/summary')
      ]);
      setContractors(contRes.data);
      setProjects(projRes.data);
      setRateContracts(rateRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Contractor Functions
  const handleContractorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContractor) {
        await apiClient.put(`/contractors/${editingContractor.id}`, contractorForm);
        toast.success("Contractor updated");
      } else {
        await apiClient.post('/contractors', contractorForm);
        toast.success("Contractor added");
      }
      setContractorDialogOpen(false);
      setEditingContractor(null);
      resetContractorForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contractor");
    }
  };

  const handleEditContractor = (contractor) => {
    setEditingContractor(contractor);
    setContractorForm({
      contractor_code: contractor.contractor_code,
      name: contractor.name,
      contact_person: contractor.contact_person,
      phone: contractor.phone,
      email: contractor.email || "",
      address: contractor.address,
      city: contractor.city,
      state: contractor.state,
      pincode: contractor.pincode,
      gst_number: contractor.gst_number,
      pan_number: contractor.pan_number,
      bank_name: contractor.bank_name,
      bank_account: contractor.bank_account,
      ifsc_code: contractor.ifsc_code,
      work_categories: contractor.work_categories || [],
      registration_number: contractor.registration_number || "",
      license_number: contractor.license_number || "",
      license_expiry: contractor.license_expiry || ""
    });
    setContractorDialogOpen(true);
  };

  const handleDeleteContractor = async (id, name) => {
    if (!window.confirm(`Deactivate contractor "${name}"?`)) return;
    try {
      await apiClient.delete(`/contractors/${id}`);
      toast.success("Contractor deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate contractor");
    }
  };

  const resetContractorForm = () => {
    setContractorForm({
      contractor_code: "",
      name: "",
      contact_person: "",
      phone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      gst_number: "",
      pan_number: "",
      bank_name: "",
      bank_account: "",
      ifsc_code: "",
      work_categories: [],
      registration_number: "",
      license_number: "",
      license_expiry: ""
    });
  };

  const toggleCategory = (cat) => {
    setContractorForm(prev => ({
      ...prev,
      work_categories: prev.work_categories.includes(cat)
        ? prev.work_categories.filter(c => c !== cat)
        : [...prev.work_categories, cat]
    }));
  };

  // Rate Contract Functions
  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/rate-contracts', {
        ...rateForm,
        rate: parseFloat(rateForm.rate)
      });
      toast.success("Rate contract created");
      setRateDialogOpen(false);
      setRateForm({
        contractor_id: "",
        project_id: "",
        work_category: "",
        description: "",
        unit: "Cum",
        rate: "",
        valid_from: new Date().toISOString().split('T')[0],
        valid_to: "",
        terms_conditions: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create rate contract");
    }
  };

  const handleDeleteRate = async (id) => {
    if (!window.confirm("Delete this rate contract?")) return;
    try {
      await apiClient.delete(`/rate-contracts/${id}`);
      toast.success("Rate contract deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rate contract");
    }
  };

  // Filters
  const filteredContractors = contractors.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.contractor_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.contact_person.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || c.work_categories?.includes(categoryFilter);
    const matchesStatus = !statusFilter || c.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">CONTRACTOR MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Manage contractors and rate contracts</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Contractors</p>
          <p className="font-heading font-black text-4xl text-primary font-mono" data-testid="total-contractors">
            {stats?.total_contractors || 0}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Active</p>
          <p className="font-heading font-black text-4xl text-success font-mono" data-testid="active-contractors">
            {stats?.active_contractors || 0}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Rate Contracts</p>
          <p className="font-heading font-black text-4xl text-accent font-mono" data-testid="rate-contracts-count">
            {rateContracts.length}
          </p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Categories</p>
          <div className="text-sm space-y-1">
            {stats?.by_category && Object.entries(stats.by_category).slice(0, 3).map(([cat, count]) => (
              <div key={cat} className="flex justify-between">
                <span className="text-slate-600 truncate">{cat}</span>
                <span className="font-mono font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("contractors")}
          data-testid="tab-contractors"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "contractors" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Contractors ({contractors.length})
        </button>
        <button
          onClick={() => setActiveTab("rates")}
          data-testid="tab-rates"
          className={`pb-3 px-4 font-medium text-sm transition-colors ${
            activeTab === "rates" 
              ? "border-b-2 border-primary text-primary" 
              : "text-slate-600 hover:text-primary"
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Rate Contracts ({rateContracts.length})
        </button>
      </div>

      {/* Contractors Tab */}
      {activeTab === "contractors" && (
        <div>
          {/* Actions Bar */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, code, contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="contractor-search"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {WORK_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Blacklisted">Blacklisted</option>
            </select>
            {canEdit && (
              <Dialog open={contractorDialogOpen} onOpenChange={setContractorDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    onClick={() => { setEditingContractor(null); resetContractorForm(); }}
                    data-testid="add-contractor-button"
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Contractor</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                      {editingContractor ? 'EDIT CONTRACTOR' : 'ADD NEW CONTRACTOR'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleContractorSubmit} className="space-y-6 mt-4">
                    {/* Basic Info */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Basic Information</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contractor Code *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.contractor_code}
                            onChange={(e) => setContractorForm({...contractorForm, contractor_code: e.target.value})}
                            data-testid="contractor-code-input"
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                            placeholder="CON-001"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Company Name *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.name}
                            onChange={(e) => setContractorForm({...contractorForm, name: e.target.value})}
                            data-testid="contractor-name-input"
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contact Person *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.contact_person}
                            onChange={(e) => setContractorForm({...contractorForm, contact_person: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Phone *</label>
                          <input
                            type="tel"
                            required
                            value={contractorForm.phone}
                            onChange={(e) => setContractorForm({...contractorForm, phone: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Email</label>
                          <input
                            type="email"
                            value={contractorForm.email}
                            onChange={(e) => setContractorForm({...contractorForm, email: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Address</h4>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Address *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.address}
                            onChange={(e) => setContractorForm({...contractorForm, address: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">City *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.city}
                            onChange={(e) => setContractorForm({...contractorForm, city: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">State *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.state}
                            onChange={(e) => setContractorForm({...contractorForm, state: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Pincode *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.pincode}
                            onChange={(e) => setContractorForm({...contractorForm, pincode: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Tax & Bank */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Tax & Bank Details</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">GST Number *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.gst_number}
                            onChange={(e) => setContractorForm({...contractorForm, gst_number: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">PAN Number *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.pan_number}
                            onChange={(e) => setContractorForm({...contractorForm, pan_number: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Bank Name *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.bank_name}
                            onChange={(e) => setContractorForm({...contractorForm, bank_name: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Bank Account *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.bank_account}
                            onChange={(e) => setContractorForm({...contractorForm, bank_account: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">IFSC Code *</label>
                          <input
                            type="text"
                            required
                            value={contractorForm.ifsc_code}
                            onChange={(e) => setContractorForm({...contractorForm, ifsc_code: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Work Categories */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">Work Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {WORK_CATEGORIES.map(cat => (
                          <button
                            type="button"
                            key={cat}
                            onClick={() => toggleCategory(cat)}
                            className={`px-3 py-1 rounded-sm text-sm font-medium transition-colors ${
                              contractorForm.work_categories.includes(cat)
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* License */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-widest">License & Registration</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Registration No.</label>
                          <input
                            type="text"
                            value={contractorForm.registration_number}
                            onChange={(e) => setContractorForm({...contractorForm, registration_number: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">License No.</label>
                          <input
                            type="text"
                            value={contractorForm.license_number}
                            onChange={(e) => setContractorForm({...contractorForm, license_number: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">License Expiry</label>
                          <input
                            type="date"
                            value={contractorForm.license_expiry}
                            onChange={(e) => setContractorForm({...contractorForm, license_expiry: e.target.value})}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <button type="button" onClick={() => setContractorDialogOpen(false)} className="btn-outline">Cancel</button>
                      <button type="submit" data-testid="submit-contractor-button" className="btn-primary">
                        {editingContractor ? 'Update Contractor' : 'Add Contractor'}
                      </button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Contractors Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredContractors.map(contractor => (
              <div key={contractor.id} className="stat-card" data-testid={`contractor-card-${contractor.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="font-mono text-xs text-slate-500">{contractor.contractor_code}</span>
                    <h3 className="font-heading font-bold text-lg text-primary">{contractor.name}</h3>
                  </div>
                  <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                    contractor.status === 'Active' ? 'bg-success/20 text-success' :
                    contractor.status === 'Blacklisted' ? 'bg-error/20 text-error' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {contractor.status}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  <p className="flex items-center text-slate-600">
                    <Phone className="w-4 h-4 mr-2" />
                    {contractor.contact_person} - {contractor.phone}
                  </p>
                  <p className="flex items-center text-slate-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    {contractor.city}, {contractor.state}
                  </p>
                  {contractor.email && (
                    <p className="flex items-center text-slate-600">
                      <Mail className="w-4 h-4 mr-2" />
                      {contractor.email}
                    </p>
                  )}
                </div>

                {contractor.work_categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {contractor.work_categories.slice(0, 3).map(cat => (
                      <span key={cat} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-sm">
                        {cat}
                      </span>
                    ))}
                    {contractor.work_categories.length > 3 && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-sm">
                        +{contractor.work_categories.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {contractor.rating > 0 && (
                  <div className="flex items-center mb-4">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} className={`w-4 h-4 ${i <= contractor.rating ? 'text-warning fill-warning' : 'text-slate-300'}`} />
                    ))}
                    <span className="ml-2 text-sm text-slate-600">{contractor.rating.toFixed(1)}</span>
                  </div>
                )}

                {(canEdit || canDelete) && (
                  <div className="flex items-center space-x-2 pt-3 border-t border-slate-200">
                    {canEdit && (
                      <button
                        onClick={() => handleEditContractor(contractor)}
                        className="flex-1 btn-outline text-sm py-1"
                      >
                        <Edit className="w-3 h-3 inline mr-1" /> Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteContractor(contractor.id, contractor.name)}
                        className="p-2 hover:bg-red-100 rounded text-error"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredContractors.length === 0 && (
            <div className="text-center py-12 stat-card">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">No Contractors Found</h3>
              <p className="text-slate-600">
                {searchTerm || categoryFilter || statusFilter 
                  ? "No contractors match your filters" 
                  : "Add your first contractor to get started."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rate Contracts Tab */}
      {activeTab === "rates" && (
        <div>
          <div className="flex justify-end mb-6">
            {canEdit && (
              <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-rate-button" className="btn-primary flex items-center space-x-2">
                    <Plus className="w-4 h-4" />
                    <span>Add Rate Contract</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">ADD RATE CONTRACT</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleRateSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Contractor *</label>
                        <select
                          required
                          value={rateForm.contractor_id}
                          onChange={(e) => setRateForm({...rateForm, contractor_id: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select Contractor</option>
                          {contractors.filter(c => c.status === 'Active').map(c => (
                            <option key={c.id} value={c.id}>{c.contractor_code} - {c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project *</label>
                        <select
                          required
                          value={rateForm.project_id}
                          onChange={(e) => setRateForm({...rateForm, project_id: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select Project</option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Work Category *</label>
                        <select
                          required
                          value={rateForm.work_category}
                          onChange={(e) => setRateForm({...rateForm, work_category: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select Category</option>
                          {WORK_CATEGORIES.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unit *</label>
                        <select
                          required
                          value={rateForm.unit}
                          onChange={(e) => setRateForm({...rateForm, unit: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="Cum">Cum</option>
                          <option value="Sqm">Sqm</option>
                          <option value="Rm">Rm</option>
                          <option value="MT">MT</option>
                          <option value="Nos">Nos</option>
                          <option value="Kg">Kg</option>
                          <option value="LS">Lump Sum</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description *</label>
                      <input
                        type="text"
                        required
                        value={rateForm.description}
                        onChange={(e) => setRateForm({...rateForm, description: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Work description"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Rate (₹) *</label>
                        <input
                          type="number"
                          required
                          step="0.01"
                          value={rateForm.rate}
                          onChange={(e) => setRateForm({...rateForm, rate: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Valid From *</label>
                        <input
                          type="date"
                          required
                          value={rateForm.valid_from}
                          onChange={(e) => setRateForm({...rateForm, valid_from: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Valid To *</label>
                        <input
                          type="date"
                          required
                          value={rateForm.valid_to}
                          onChange={(e) => setRateForm({...rateForm, valid_to: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Terms & Conditions</label>
                      <textarea
                        value={rateForm.terms_conditions}
                        onChange={(e) => setRateForm({...rateForm, terms_conditions: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        rows="3"
                      />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                      <button type="button" onClick={() => setRateDialogOpen(false)} className="btn-outline">Cancel</button>
                      <button type="submit" className="btn-primary">Create Rate Contract</button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Contractor</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Project</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Description</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Rate</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Validity</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  {canDelete && <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rateContracts.map(contract => (
                  <tr key={contract.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium">{contract.contractor_name}</td>
                    <td className="py-3 px-4 text-sm">{contract.project_name}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-sm">{contract.work_category}</span>
                    </td>
                    <td className="py-3 px-4 text-sm">{contract.description}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-semibold">₹{contract.rate}/{contract.unit}</td>
                    <td className="py-3 px-4 text-center text-xs font-mono">
                      {contract.valid_from} to {contract.valid_to}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                        contract.status === 'Active' ? 'bg-success/20 text-success' :
                        contract.status === 'Expired' ? 'bg-warning/20 text-warning' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {contract.status}
                      </span>
                    </td>
                    {canDelete && (
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleDeleteRate(contract.id)}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-error" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {rateContracts.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                No rate contracts yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorManagement;
